import { ArrowUpRightIcon, GlobeIcon, SearchIcon, SparklesIcon } from "lucide-react";
import { type KeyboardEvent, useCallback, useEffect, useState } from "react";
import {
  availableBarModes,
  type BarMode,
  type BarTarget,
  nextBarMode,
  resolveBarInput,
} from "@/lib/omnibox";
import { cn } from "@/lib/utils";
import {
  dispatchSearch,
  getCurrentEngine,
} from "@/routes/dashboard/components/assistant/search-engine-picker";

/**
 * The mode of the one bar on the new tab. It follows what is typed (a URL
 * opens, a question asks, a phrase searches) until Tab picks another mode;
 * clearing the text goes back to following.
 */
export function useBarMode(text: string, canAsk: boolean) {
  const [override, setOverride] = useState<BarMode | null>(null);
  const modes = availableBarModes(text, canAsk);
  const mode: BarMode | null = override && modes.includes(override) ? override : (modes[0] ?? null);

  useEffect(() => {
    if (!text.trim()) setOverride(null);
  }, [text]);

  const cycle = useCallback(() => {
    if (mode && modes.length > 1) setOverride(nextBarMode(mode, modes));
  }, [mode, modes]);

  /** Tab switches mode while there is text to act on; otherwise focus moves on as usual. */
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== "Tab" || e.shiftKey || e.altKey || e.metaKey || e.ctrlKey) return;
      if (modes.length < 2) return;
      e.preventDefault();
      cycle();
    },
    [cycle, modes.length],
  );

  const target: BarTarget | null = mode ? resolveBarInput(text, mode) : null;

  return { mode, modes, cycle, onKeyDown, target };
}

/** Opens a URL or runs a web search; `ask` targets are sent by the composer instead. */
export function openBarTarget(target: BarTarget | null, workspaceId?: string | null) {
  if (!target) return;
  if (target.kind === "url") window.location.href = target.url;
  else if (target.kind === "search") dispatchSearch(target.query, workspaceId);
}

function barModeLabel(mode: BarMode): string {
  if (mode === "url") return "Open";
  if (mode === "ask") return "Ask";
  const engine = getCurrentEngine();
  return engine ? `Search ${engine.label}` : "Search";
}

const MODE_ICON = { url: GlobeIcon, search: SearchIcon, ask: SparklesIcon } as const;

/**
 * Shows what Enter will do. Clicking it does what Tab does, so the mode can be
 * switched without the keyboard.
 */
export function BarModeChip({
  mode,
  canCycle,
  onCycle,
  className,
}: {
  mode: BarMode | null;
  canCycle: boolean;
  onCycle: () => void;
  className?: string;
}) {
  if (!mode) return null;
  const Icon = MODE_ICON[mode];
  return (
    <button
      type="button"
      onClick={onCycle}
      disabled={!canCycle}
      aria-live="polite"
      title={canCycle ? "Switch mode (Tab)" : undefined}
      className={cn(
        "border-border text-foreground flex h-7 shrink-0 items-center gap-1.5 border px-2 text-xs transition-colors enabled:hover:border-foreground disabled:cursor-default",
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden="true" />
      <span>{barModeLabel(mode)}</span>
      {canCycle && (
        <span className="text-muted-foreground text-[11px]" aria-hidden="true">
          Tab
        </span>
      )}
    </button>
  );
}

/** The submit button for `url` and `search`, in place of the composer's send. */
export function BarGoButton({ mode, onClick }: { mode: "url" | "search"; onClick: () => void }) {
  const Icon = mode === "url" ? ArrowUpRightIcon : SearchIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={barModeLabel(mode)}
      title={barModeLabel(mode)}
      className="bg-primary text-primary-foreground hover:bg-brand hover:text-brand-foreground flex size-8 items-center justify-center transition-colors"
    >
      <Icon className="size-4.5" aria-hidden="true" />
    </button>
  );
}
